import { useEffect, useMemo, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { ItemCombobox } from "@/components/ItemCombobox";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tag, Plus, Pencil, Trash2, Printer, ArrowLeft, X, Save } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const SIZES = [
  { key: "small", label: "Small (50 × 25 mm)", w: "50mm", h: "25mm", qr: 34, fs: "7px" },
  { key: "medium", label: "Medium (75 × 38 mm)", w: "75mm", h: "38mm", qr: 52, fs: "9px" },
  { key: "large", label: "Large (100 × 50 mm)", w: "100mm", h: "50mm", qr: 70, fs: "11px" },
];
const sizeOf = (k) => SIZES.find(s => s.key === k) || SIZES[1];

const emptyHdr = () => ({ name: "", created_date: new Date().toISOString().slice(0, 10), description: "" });
const emptyLine = () => ({ item_id: "", rate: "", print_qty: 1 });

// Printable sheet — rendered in DOM but hidden on screen, shown only for print
function LabelSheet({ batch }) {
  const sz = sizeOf(batch.label_size);
  const labels = [];
  (batch.items || []).forEach(l => {
    const qty = Math.max(1, Number(l.print_qty) || 1);
    for (let i = 0; i < qty; i++) labels.push({ ...l, _k: `${l.item_id}-${i}` });
  });
  return (
    <div className="hidden print:block print:bg-white" data-testid="label-sheet">
      <div className="label-grid">
        {labels.map(l => (
          <div key={l._k} className="label-cell" style={{ width: sz.w, height: sz.h }}>
            <div className="text-center font-bold uppercase border-b border-black" style={{ fontSize: sz.fs }}>{batch.company || "SynqMoto ERP"}</div>
            <div className="flex items-center gap-1 p-1">
              <QRCodeSVG value={String(l.part_number || l.item_id)} size={sz.qr}/>
              <div className="text-black leading-tight min-w-0" style={{ fontSize: sz.fs }}>
                <div className="font-bold truncate">{l.item_name}</div>
                <div className="font-mono">{l.part_number}</div>
                <div className="font-mono">MRP ₹{l.mrp}{l.rate !== "" && l.rate != null ? ` · Rate ₹${l.rate}` : ""}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <style>{`
        @media print {
          @page { size: A4; margin: 6mm; }
          .label-grid { display: flex; flex-wrap: wrap; gap: 2mm; }
          .label-cell { page-break-inside: avoid; background: white; color: black; border: 1px solid black; border-radius: 2px; overflow: hidden; }
        }
      `}</style>
    </div>
  );
}

export default function LabelCreation() {
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [listFilter, setListFilter] = useState({ company: "all", group: "all" });
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [hdr, setHdr] = useState(emptyHdr());
  const [filters, setFilters] = useState({ company: "all", category: "all", group: "all" });
  const [lines, setLines] = useState([emptyLine()]);
  const [labelSize, setLabelSize] = useState("medium");
  const [saving, setSaving] = useState(false);
  const [printBatch, setPrintBatch] = useState(null);

  const load = () => api.get("/label-batches").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => {
    load();
    api.get("/items?scope=all").then(r => setItems(r.data)).catch(() => {});
    api.get("/companies").then(r => setCompanies(r.data)).catch(() => {});
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/groups").then(r => setGroups(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!printBatch) return;
    const t = setTimeout(() => window.print(), 350);
    return () => clearTimeout(t);
  }, [printBatch]);

  const filtered = useMemo(() => list.filter(b =>
    (listFilter.company === "all" || b.company === listFilter.company) &&
    (listFilter.group === "all" || b.group === listFilter.group)
  ), [list, listFilter]);
  const pg = usePagination(filtered, filters);

  const openNew = () => { setHdr(emptyHdr()); setFilters({ company: "all", category: "all", group: "all" }); setLines([emptyLine()]); setLabelSize("medium"); setEditId(null); setOpen(true); };
  const openEdit = (b) => {
    setHdr({ name: b.name, created_date: b.created_date || "", description: b.description || "" });
    setFilters({ company: b.company || "all", category: b.category || "all", group: b.group || "all" });
    setLabelSize(b.label_size || "medium");
    const ls = (b.items || []).map(l => ({ item_id: l.item_id, rate: l.rate ?? "", print_qty: l.print_qty ?? 1 }));
    setLines(ls.length ? ls : [emptyLine()]);
    setEditId(b.id); setOpen(true);
  };
  const closeForm = () => { setOpen(false); setEditId(null); };

  const setLine = (i, k, v) => setLines(ls => ls.map((l, x) => x === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines(ls => [...ls, emptyLine()]);
  const removeLine = (i) => setLines(ls => ls.filter((_, x) => x !== i));

  const loadItems = () => {
    const match = items.filter(it =>
      (filters.company === "all" || it.company === filters.company) &&
      (filters.category === "all" || it.category === filters.category) &&
      (filters.group === "all" || it.group === filters.group));
    setLines(match.map(it => ({ item_id: it.id, rate: "", print_qty: 1 })));
    toast.success(`Loaded ${match.length} item${match.length === 1 ? "" : "s"}`);
  };

  const buildBatch = () => ({
    ...hdr,
    description: hdr.description || null,
    company: filters.company === "all" ? null : filters.company,
    category: filters.category === "all" ? null : filters.category,
    group: filters.group === "all" ? null : filters.group,
    label_size: labelSize,
    status: "Active",
    items: lines.filter(l => l.item_id).map(l => {
      const it = items.find(x => x.id === l.item_id) || {};
      return {
        item_id: l.item_id, item_name: it.item_name || "", part_number: it.part_number || "",
        hsn_code: it.hsn_code || "", mrp: it.mrp ?? 0, purchase_rate: it.purchase_rate || it.per_unit_cost || 0,
        rate: l.rate === "" ? null : Number(l.rate), print_qty: Number(l.print_qty) || 1,
      };
    }),
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildBatch();
      if (editId) await api.patch(`/label-batches/${editId}`, payload);
      else await api.post("/label-batches", payload);
      toast.success(editId ? "Label updated" : "Label created");
      closeForm(); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (b) => {
    if (!confirm(`Delete label batch "${b.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/label-batches/${b.id}`); toast.success("Label batch deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const printCurrent = () => {
    const b = buildBatch();
    if (!b.items.length) return toast.error("Load items before printing labels");
    setPrintBatch(b);
  };

  // ---------- Form view ----------
  if (open) {
    return (
      <div className="p-8" onKeyDownCapture={handleSimpleFormNav}>
        <div className="border border-border bg-card rounded-sm p-6 min-h-[70vh] print:hidden" data-testid="label-form-page">
          <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
            <div className="flex items-center gap-3">
              <Button type="button" data-testid="lc-back-btn" variant="outline" onClick={closeForm} className="rounded-sm text-xs uppercase tracking-wider"><ArrowLeft className="w-3 h-3 mr-1"/>Back to List</Button>
              <h2 className="font-display uppercase text-lg">{editId ? "Edit" : "New"} Label</h2>
            </div>
            <Button type="button" data-testid="lc-cancel-btn" variant="ghost" onClick={closeForm} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground"><X className="w-3 h-3 mr-1"/>Cancel</Button>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Label Name *</Label>
              <Input data-testid="lc-name" value={hdr.name} onChange={(e) => setHdr(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. Battery Shelf Labels"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Created Date</Label>
              <Input data-testid="lc-date" type="date" value={hdr.created_date} onChange={(e) => setHdr(x => ({ ...x, created_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
              <Input data-testid="lc-desc" value={hdr.description} onChange={(e) => setHdr(x => ({ ...x, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="Optional note"/>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 mb-2 p-3 border border-border bg-card rounded-sm" data-testid="lc-filter-bar">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Company</Label>
              <Select value={filters.company} onValueChange={(v) => setFilters(x => ({ ...x, company: v }))}>
                <SelectTrigger data-testid="lc-filter-company" className="mt-1 rounded-sm bg-secondary border-border w-44"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem data-testid="lc-filter-company-opt-all" value="all">All companies</SelectItem>{companies.map(c => <SelectItem key={c.id} data-testid={`lc-filter-company-opt-${c.id}`} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
              <Select value={filters.category} onValueChange={(v) => setFilters(x => ({ ...x, category: v }))}>
                <SelectTrigger data-testid="lc-filter-category" className="mt-1 rounded-sm bg-secondary border-border w-44"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem data-testid="lc-filter-category-opt-all" value="all">All categories</SelectItem>{categories.map(c => <SelectItem key={c.id} data-testid={`lc-filter-category-opt-${c.id}`} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Group</Label>
              <Select value={filters.group} onValueChange={(v) => setFilters(x => ({ ...x, group: v }))}>
                <SelectTrigger data-testid="lc-filter-group" className="mt-1 rounded-sm bg-secondary border-border w-44"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem data-testid="lc-filter-group-opt-all" value="all">All groups</SelectItem>{groups.map(g => <SelectItem key={g.id} data-testid={`lc-filter-group-opt-${g.id}`} value={g.name}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button type="button" data-testid="lc-load-items" variant="outline" onClick={loadItems} className="rounded-sm text-xs uppercase tracking-wider">Load Items</Button>
          </div>

          <div className="border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  <th className="text-left p-2">Item Name</th>
                  <th className="text-left p-2 w-28">Part No</th>
                  <th className="text-left p-2 w-24">HSN Code</th>
                  <th className="text-right p-2 w-24">MRP</th>
                  <th className="text-right p-2 w-24">Purchase Rate</th>
                  <th className="text-right p-2 w-24">Rate</th>
                  <th className="text-center p-2 w-20">Barcode</th>
                  <th className="text-right p-2 w-20">Print Qty</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const it = items.find(x => x.id === l.item_id);
                  return (
                    <tr key={i} data-testid={`lc-line-${i}`} className="border-t border-border">
                      <td className="p-1">
                        <ItemCombobox items={items} value={l.item_id} onSelect={(x) => setLine(i, "item_id", x.id)} testId={`lc-item-${i}`}
                          labelFn={(x) => `${x.item_code ? x.item_code + " · " : ""}${x.item_name} · MRP ₹${x.mrp}`}/>
                      </td>
                      <td className="p-1"><Input data-testid={`lc-partno-${i}`} value={it?.part_number || ""} readOnly className="h-9 rounded-sm bg-secondary border-border text-xs font-mono text-muted-foreground"/></td>
                      <td className="p-1"><Input data-testid={`lc-hsn-${i}`} value={it?.hsn_code || ""} readOnly className="h-9 rounded-sm bg-secondary border-border text-xs font-mono text-muted-foreground"/></td>
                      <td className="p-1"><Input data-testid={`lc-mrp-${i}`} value={it ? inr(it.mrp ?? 0) : ""} readOnly className="h-9 rounded-sm bg-secondary border-border text-xs text-right font-mono text-muted-foreground"/></td>
                      <td className="p-1"><Input data-testid={`lc-prate-${i}`} value={it ? inr(it.purchase_rate || it.per_unit_cost || 0) : ""} readOnly className="h-9 rounded-sm bg-secondary border-border text-xs text-right font-mono text-muted-foreground"/></td>
                      <td className="p-1"><Input data-testid={`lc-rate-${i}`} type="number" min="0" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} className="h-9 rounded-sm bg-secondary border-border text-xs text-right"/></td>
                      <td className="p-1 text-center">{it?.part_number && <div className="inline-block bg-white rounded-sm p-0.5"><QRCodeSVG value={String(it.part_number)} size={28}/></div>}</td>
                      <td className="p-1"><Input data-testid={`lc-qty-${i}`} type="number" min="1" value={l.print_qty} onChange={(e) => setLine(i, "print_qty", e.target.value)} className="h-9 rounded-sm bg-secondary border-border text-xs text-right w-20 ml-auto"/></td>
                      <td className="p-1 text-center">
                        {lines.length > 1 && <Button type="button" data-testid={`lc-del-line-${i}`} size="sm" variant="ghost" onClick={() => removeLine(i)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Button type="button" data-testid="lc-add-line" variant="outline" onClick={addLine} className="mt-2 rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/>Add Item</Button>

          <div className="flex items-end justify-between gap-3 mt-6 border-t border-border pt-4 flex-wrap">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Label Size</Label>
              <Select value={labelSize} onValueChange={setLabelSize}>
                <SelectTrigger data-testid="lc-size" className="mt-1 rounded-sm bg-secondary border-border w-56"><SelectValue/></SelectTrigger>
                <SelectContent>{SIZES.map(s => <SelectItem key={s.key} data-testid={`lc-size-opt-${s.key}`} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={closeForm} className="rounded-sm text-xs uppercase tracking-wider">Cancel</Button>
              <Button data-testid="lc-print-btn" variant="outline" onClick={printCurrent} disabled={!lines.some(l => l.item_id)} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print Labels</Button>
              <Button data-testid="lc-save-btn" onClick={save} disabled={saving || !hdr.name.trim() || !lines.some(l => l.item_id)} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider"><Save className="w-3 h-3 mr-1"/> {editId ? "Update Label" : "Save Label"}</Button>
            </div>
          </div>
        </div>
        {printBatch && <LabelSheet batch={printBatch}/>}
      </div>
    );
  }

  // ---------- List view ----------
  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 print:hidden">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Tag className="w-3 h-3"/> Inventory · Masters</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Label Creation</h1>
          <div className="text-xs text-muted-foreground mt-1">Create and print item label batches</div>
        </div>
        <Button data-testid="lc-new-btn" onClick={openNew} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Plus className="w-4 h-4 mr-1"/> New Label</Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-3 border border-border bg-card p-3 rounded-sm print:hidden">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Company</Label>
          <Select value={listFilter.company} onValueChange={(v) => setListFilter(x => ({ ...x, company: v }))}>
            <SelectTrigger data-testid="lc-list-company" className="mt-1 rounded-sm bg-secondary border-border h-9 w-48"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All companies</SelectItem>{companies.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Group</Label>
          <Select value={listFilter.group} onValueChange={(v) => setListFilter(x => ({ ...x, group: v }))}>
            <SelectTrigger data-testid="lc-list-group" className="mt-1 rounded-sm bg-secondary border-border h-9 w-48"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All groups</SelectItem>{groups.map(g => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto print:hidden">
        <table className="w-full text-sm" data-testid="lc-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Label Name</th>
              <th className="text-left p-3">Company</th>
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Item Count</th>
              <th className="text-left p-3">Created Date</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground" data-testid="lc-empty">No label batches yet — click New Label to create one</td></tr>}
            {pg.pageItems.map(b => (
              <tr key={b.id} data-testid={`lc-row-${b.id}`} className="border-t border-border hover:bg-secondary/40">
                <td className="p-3 font-semibold">{b.name}</td>
                <td className="p-3 text-xs">{b.company || "-"}</td>
                <td className="p-3 text-xs">{b.category || "-"}</td>
                <td className="p-3 text-right font-mono">{(b.items || []).length}</td>
                <td className="p-3 text-xs font-mono">{b.created_date || (b.created_at || "").slice(0, 10)}</td>
                <td className="p-3"><span className="px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider border text-emerald-500 bg-emerald-500/10 border-emerald-500/30">{b.status || "Active"}</span></td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button data-testid={`lc-edit-${b.id}`} size="sm" variant="ghost" title="Edit" onClick={() => openEdit(b)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`lc-print-${b.id}`} size="sm" variant="ghost" title="Print" onClick={() => setPrintBatch(b)} className="h-7 w-7 p-0"><Printer className="w-3 h-3"/></Button>
                  <Button data-testid={`lc-del-${b.id}`} size="sm" variant="ghost" title="Delete" onClick={() => del(b)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
      {printBatch && <LabelSheet batch={printBatch}/>}
    </div>
  );
}
